import { useI18n } from '@/hooks/useI18n';
import { useMemo, useState } from 'react';
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Skeleton } from '@/components/ui/skeleton';
import type { TimeSeries } from '@/types/api';
import { formatDate, formatNumber } from '@/lib/format';
import { cn } from '@/lib/cn';

interface FollowersViewsChartProps {
  followers: TimeSeries | undefined;
  views: TimeSeries | undefined;
  loading?: boolean;
}

type SeriesKey = 'followers' | 'views';

const SERIES: Record<SeriesKey, { label: string; labelEn: string; color: string; gradId: string }> =
  {
    followers: {
      label: 'Abonnés',
      labelEn: 'Followers',
      color: 'hsl(258 90% 66%)',
      gradId: 'followers-grad',
    },
    views: {
      label: 'Vues',
      labelEn: 'Views',
      color: 'hsl(152 72% 42%)',
      gradId: 'views-grad',
    },
  };

interface MergedPoint {
  t: string;
  followers: number | null;
  views: number | null;
}

export function FollowersViewsChart({ followers, views, loading }: FollowersViewsChartProps) {
  const { t } = useI18n();
  const [visible, setVisible] = useState<Record<SeriesKey, boolean>>({
    followers: true,
    views: true,
  });

  const points = useMemo<MergedPoint[]>(() => {
    const map = new Map<string, MergedPoint>();
    for (const p of followers?.points ?? []) {
      map.set(p.timestamp, { t: p.timestamp, followers: p.value, views: null });
    }
    for (const p of views?.points ?? []) {
      const existing = map.get(p.timestamp);
      if (existing) {
        existing.views = p.value;
      } else {
        map.set(p.timestamp, { t: p.timestamp, followers: null, views: p.value });
      }
    }
    return Array.from(map.values()).sort((a, b) => (a.t < b.t ? -1 : a.t > b.t ? 1 : 0));
  }, [followers, views]);

  const latest = useMemo(() => {
    const last = (key: SeriesKey) => {
      for (let i = points.length - 1; i >= 0; i -= 1) {
        const v = points[i][key];
        if (v !== null) return v;
      }
      return null;
    };
    return { followers: last('followers'), views: last('views') };
  }, [points]);

  const toggle = (key: SeriesKey) => {
    setVisible((prev) => {
      const next = { ...prev, [key]: !prev[key] };
      if (!next.followers && !next.views) {
        return { ...prev, [key]: true };
      }
      return next;
    });
  };

  if (loading) {
    return <Skeleton className="h-[320px] w-full rounded-[14px]" />;
  }

  if (points.length === 0) {
    return (
      <div className="flex h-[280px] items-center justify-center rounded-[14px] border border-dashed border-line-2 bg-bg-0 px-4">
        <div className="space-y-1 text-center">
          <div className="text-[12px] font-semibold text-text-2">
            {t('Aucun historique', 'No history')}
          </div>
          <p className="text-[12px] text-text-3">
            {t(
              'Lancez un scrape pour commencer la collecte.',
              'Run a scrape to start collecting data.',
            )}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2">
        {(Object.keys(SERIES) as SeriesKey[]).map((key) => {
          const isOn = visible[key];
          const value = latest[key];
          return (
            <button
              key={key}
              type="button"
              onClick={() => toggle(key)}
              aria-pressed={isOn}
              className={cn(
                'flex min-h-11 items-center gap-2 rounded-[10px] border border-line-1 bg-bg-0 px-3 py-1.5 text-[12px] transition-all sm:min-h-0',
                'hover:border-line-2 hover:bg-bg-2',
                !isOn && 'opacity-40',
              )}
            >
              <span
                className="h-2.5 w-2.5 rounded-[3px]"
                style={{ background: SERIES[key].color }}
              />
              <span className="font-medium text-text-1">
                {t(SERIES[key].label, SERIES[key].labelEn)}
              </span>
              <span className="tabular-nums text-text-3">
                {value !== null ? formatNumber(value) : '—'}
              </span>
            </button>
          );
        })}
      </div>

      <div className="h-[240px] w-full min-w-0 sm:h-[280px]">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={points} margin={{ top: 16, right: 8, left: -18, bottom: 0 }}>
            <defs>
              <linearGradient id={SERIES.followers.gradId} x1="0" x2="0" y1="0" y2="1">
                <stop offset="0%" stopColor={SERIES.followers.color} stopOpacity={0.35} />
                <stop offset="100%" stopColor={SERIES.followers.color} stopOpacity={0} />
              </linearGradient>
              <linearGradient id={SERIES.views.gradId} x1="0" x2="0" y1="0" y2="1">
                <stop offset="0%" stopColor={SERIES.views.color} stopOpacity={0.35} />
                <stop offset="100%" stopColor={SERIES.views.color} stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid stroke="hsl(30 14% 90%)" strokeDasharray="3 4" vertical={false} />
            <XAxis
              dataKey="t"
              tick={{ fill: 'hsl(240 5% 55%)', fontSize: 10 }}
              stroke="transparent"
              tickFormatter={(v) => {
                try {
                  return formatDate(v, 'dd MMM');
                } catch {
                  return '';
                }
              }}
              minTickGap={40}
              axisLine={false}
              tickLine={false}
            />
            <YAxis
              yAxisId="followers"
              orientation="left"
              tick={{ fill: 'hsl(240 5% 55%)', fontSize: 10 }}
              stroke="transparent"
              width={56}
              tickFormatter={(v) => formatNumber(v)}
              axisLine={false}
              tickLine={false}
              hide={!visible.followers}
              domain={['auto', 'auto']}
            />
            <YAxis
              yAxisId="views"
              orientation="right"
              tick={{ fill: 'hsl(240 5% 55%)', fontSize: 10 }}
              stroke="transparent"
              width={56}
              tickFormatter={(v) => formatNumber(v)}
              axisLine={false}
              tickLine={false}
              hide={!visible.views}
              domain={['auto', 'auto']}
            />
            <Tooltip
              content={<DualTooltip visible={visible} />}
              cursor={{ stroke: 'hsl(240 5% 55%)', strokeDasharray: '3 3' }}
            />
            <Area
              yAxisId="followers"
              type="monotone"
              dataKey="followers"
              name={t(SERIES.followers.label, SERIES.followers.labelEn)}
              stroke={SERIES.followers.color}
              strokeWidth={2}
              fill={`url(#${SERIES.followers.gradId})`}
              connectNulls={false}
              hide={!visible.followers}
              isAnimationActive
              animationDuration={400}
            />
            <Area
              yAxisId="views"
              type="monotone"
              dataKey="views"
              name={t(SERIES.views.label, SERIES.views.labelEn)}
              stroke={SERIES.views.color}
              strokeWidth={2}
              fill={`url(#${SERIES.views.gradId})`}
              connectNulls={false}
              hide={!visible.views}
              isAnimationActive
              animationDuration={400}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

interface TooltipPayloadItem {
  value: number | null;
  dataKey: string;
  name: string;
  color: string;
}

function DualTooltip({
  active,
  payload,
  label,
  visible,
}: {
  active?: boolean;
  payload?: TooltipPayloadItem[];
  label?: string | number;
  visible: Record<SeriesKey, boolean>;
}) {
  useI18n();
  if (!active || !payload?.length) return null;

  const rows = payload.filter((p) => {
    const key = p.dataKey as SeriesKey;
    return visible[key] && p.value !== null && p.value !== undefined;
  });

  if (rows.length === 0) return null;

  return (
    <div className="rounded-[10px] border border-line-1 bg-bg-1 px-3 py-2 shadow-card">
      <div className="text-[10px] font-semibold uppercase tracking-widest text-text-3">
        {label ? formatDate(String(label)) : '—'}
      </div>
      <div className="mt-1 space-y-1">
        {rows.map((row) => (
          <div key={row.dataKey} className="flex items-center gap-2">
            <span className="h-2 w-2 rounded-[2px]" style={{ background: row.color }} />
            <span className="text-[11px] text-text-3">{row.name}</span>
            <span className="font-display ml-auto text-[13px] tabular-nums text-text-1">
              {formatNumber(row.value as number)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
